import { z } from "zod";

import {
	CompanyOpenTicketCount,
	companyOpenTicketCountSchema,
	CreateStaffTicketCommentRequest,
	CreateStaffTicketRequest,
	staffAssigneeListSchema,
	StaffTicket,
	StaffTicketDetail,
	staffTicketDetailSchema,
	StaffTicketListResponse,
	staffTicketListResponseSchema,
	StaffTicketReplyDispatchResponse,
	staffTicketReplyDispatchResponseSchema,
	StaffTicketReplyResponse,
	staffTicketReplyResponseSchema,
	staffTicketSchema,
	StaffTicketStats,
	staffTicketStatsSchema,
	TicketAttachmentMeta,
	ticketAttachmentMetaSchema,
	TicketUserRef,
	UpdateStaffTicketRequest,
} from "@signalist/shared-types";

import { api } from "@/redux/api";

import { GetStaffTicketsArg, toStaffTicketParams } from "./tickets-admin.params";

/**
 * The staff side of the ticket system (PREC-3934's `tickets/admin` routes; PREC-3939 adds stats and
 * assignees; PREC-3940 the detail, the comment, the PATCH and the blob read). Permission-gated
 * only — every consumer must `skip` unless the user holds GLOBAL_ADMIN, or the API answers 403.
 * The queue is server-driven: filters go out as params and the page comes back sorted (URGENT →
 * LOW, then waiting longest); nothing re-sorts or re-filters it here.
 */
export const ticketsAdminApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getStaffTickets: builder.query<StaffTicketListResponse, GetStaffTicketsArg>({
			query: (arg) => ({
				url: "tickets/admin",
				params: toStaffTicketParams(arg),
				responseSchema: staffTicketListResponseSchema,
			}),
			providesTags: ["Tickets"],
		}),
		/** The strip, the tab counts and the sidebar badge — one cache entry for all three. */
		getStaffTicketStats: builder.query<StaffTicketStats, void>({
			query: () => ({ url: "tickets/admin/stats", responseSchema: staffTicketStatsSchema }),
			providesTags: ["Tickets"],
		}),
		/** The roster changes with roles, not tickets: no tag, the default cache lifetime applies. */
		getStaffAssignees: builder.query<TicketUserRef[], void>({
			query: () => ({ url: "tickets/admin/assignees", responseSchema: staffAssigneeListSchema }),
		}),
		/** Support Dashboard rows: open and waiting-on-us counts per company. */
		getCompanyTicketCounts: builder.query<CompanyOpenTicketCount[], void>({
			query: () => ({
				url: "tickets/admin/counts",
				responseSchema: z.array(companyOpenTicketCountSchema),
			}),
			providesTags: ["Tickets"],
		}),
		/** One ticket with its full thread, internal notes included; an unknown id is 404. */
		getStaffTicket: builder.query<StaffTicketDetail, number>({
			query: (id) => ({ url: `tickets/admin/${id}`, responseSchema: staffTicketDetailSchema }),
			providesTags: (_result, _error, id) => [{ type: "Tickets", id }],
		}),
		/**
		 * A public reply or an internal note. `status` is the composer's "Then set" — one action, one
		 * STATUS_CHANGE row (PREC-3934 decision 7). Answers the ticket plus the row it wrote, so the
		 * composer can upload against `comment.id` (PREC-3940 decision 1).
		 */
		addStaffTicketComment: builder.mutation<
			StaffTicketReplyResponse,
			{ ticketId: number } & CreateStaffTicketCommentRequest
		>({
			query: ({ ticketId, ...body }) => ({
				url: `tickets/admin/${ticketId}/comments`,
				method: "POST",
				body,
				responseSchema: staffTicketReplyResponseSchema,
			}),
			invalidatesTags: ["Tickets"],
		}),
		/**
		 * Triage: any subset of status / assigneeUserId (null unassigns) / priority / category. Only a
		 * status change writes a thread row; 400 TICKET_ASSIGNEE_NOT_STAFF for a non-staff assignee.
		 */
		updateStaffTicket: builder.mutation<
			StaffTicket,
			{ ticketId: number } & UpdateStaffTicketRequest
		>({
			query: ({ ticketId, ...body }) => ({
				url: `tickets/admin/${ticketId}`,
				method: "PATCH",
				body,
				responseSchema: staffTicketSchema,
			}),
			invalidatesTags: ["Tickets"],
		}),
		/** The staff twin of the customer blob read (`tickets.api.ts`): same arg, same object-URL contract. */
		getStaffTicketAttachmentBlob: builder.query<Blob, { ticketId: number; attachmentId: number }>({
			query: ({ ticketId, attachmentId }) => ({
				url: `tickets/admin/${ticketId}/attachments/${attachmentId}`,
				responseHandler: async (response: Response) => {
					if (!response.ok) {
						return { error: response.statusText, status: response.status };
					}

					return response.blob();
				},
			}),
		}),
		/** File on a customer's behalf; 400 TICKET_CUSTOMER_NOT_IN_COMPANY when they do not match. */
		createStaffTicket: builder.mutation<StaffTicket, CreateStaffTicketRequest>({
			query: (body) => ({
				url: "tickets/admin",
				method: "POST",
				body,
				responseSchema: staffTicketSchema,
			}),
			invalidatesTags: ["Tickets"],
		}),
		/** The staff twin of the customer upload: same one-request-per-file contract. */
		uploadStaffTicketAttachment: builder.mutation<
			TicketAttachmentMeta,
			{ ticketId: number; file: File; commentId?: number }
		>({
			query: ({ ticketId, file, commentId }) => {
				const formData = new FormData();

				formData.append("file", file);

				if (commentId !== undefined) {
					formData.append("commentId", String(commentId));
				}

				return {
					url: `tickets/admin/${ticketId}/attachments`,
					method: "POST",
					body: formData,
					responseSchema: ticketAttachmentMetaSchema,
				};
			},
			invalidatesTags: ["Tickets"],
		}),
		/**
		 * Sends the reply's support mail, after its files are uploaded (PREC-4088).
		 *
		 * The comment POST deliberately does not mail — at that point the attachments do not exist,
		 * which is how a support agent's PDF was stored and never delivered. Idempotent server-side,
		 * so a retry cannot double-mail; `sent: false` is an ordinary answer for a reply that needs
		 * no support mail at all.
		 */
		dispatchStaffTicketReply: builder.mutation<
			StaffTicketReplyDispatchResponse,
			{ ticketId: number; commentId: number }
		>({
			query: ({ ticketId, commentId }) => ({
				url: `tickets/admin/${ticketId}/comments/${commentId}/dispatch`,
				method: "POST",
				responseSchema: staffTicketReplyDispatchResponseSchema,
			}),
			invalidatesTags: ["Tickets"],
		}),
	}),
});

export const {
	useGetStaffTicketsQuery,
	useGetStaffTicketStatsQuery,
	useGetStaffAssigneesQuery,
	useGetCompanyTicketCountsQuery,
	useGetStaffTicketQuery,
	useAddStaffTicketCommentMutation,
	useUpdateStaffTicketMutation,
	useGetStaffTicketAttachmentBlobQuery,
	useLazyGetStaffTicketAttachmentBlobQuery,
	useCreateStaffTicketMutation,
	useUploadStaffTicketAttachmentMutation,
	useDispatchStaffTicketReplyMutation,
} = ticketsAdminApi;
